//    _____ ____  _   _  _____ _______       _   _ _______ _____
//   / ____/ __ \| \ | |/ ____|__   __|/\   | \ | |__   __/ ____|
//  | |   | |  | |  \| | (___    | |  /  \  |  \| |  | | | (___
//  | |   | |  | | . ` |\___ \   | | / /\ \ | . ` |  | |  \___ \
//  | |___| |__| | |\  |____) |  | |/ ____ \| |\  |  | |  ____) |
//   \_____\____/|_| \_|_____/   |_/_/    \_\_| \_|  |_| |_____/

import { IGTagService, ITaboolaService } from 'Services'
import { CustomPackage } from 'types/onboardingTypes'
import * as Sentry from '@sentry/react'
import { getLocalId } from 'Lib/AppUtils'
import invariant from 'invariant'
import { ISnapchatService } from 'Services/Snapchat.service'
import { trackFacebookEvent } from 'Services/Facebook.service'
import { trackNewsBreakEvent } from 'Services/NewsBreak.service'
import { trackTwitterEvent, TWITTER_EVENTS } from 'Services/Twitter.service'
import { trackTikTokConversionEvent } from 'Services/TikTok.service'
import { trackOpenAiAdsConversionEvent } from 'Services/OpenAiAds.service'
import { callAppsFlyer } from 'Services/AppsFlyerService'

/**
 * This events also need to be sent to other analytic services
 * e.g. Facebook payment tracking, Appsflyer conversion, etc.
 */
const THIRD_PARTY_SUPPORTED_EVENTS: AnalyticEvents[] | string[] = [
  'web_commit_1_gender',
  'web_commit_33_email',
  'web_startCheckout',
  'web_purchaseSuccess',
  'web_signUpSuccess'
]

function logEventToThirdParty(gTagService: IGTagService, taboolaService: ITaboolaService | null, snapchatService: ISnapchatService | null, eventName: AnalyticEvents | string, params: any) { // Pinterest service is removed temporarily.
  // TikTok has its own dev pixel and is meant to fire off production. It stays
  // above the gate below, and catches its own errors so it cannot starve the rest.
  trackTikTokConversionEvent(eventName, params)

  // Above the gate for the same reason: off production its Pixel half self-gates
  // on DISABLE_ON_DEV, but the server half has to keep firing so staging exercises
  // it — the Cloud Function sends those as validate_only.
  trackOpenAiAdsConversionEvent(eventName, params)

  // Everything past here is production-only: the AF and gTagService calls have no
  // dev guard of their own, unlike the services that self-gate on DISABLE_ON_DEV.
  if (__DEV__) {
    return
  }

  if (eventName === 'web_purchaseSuccess') {
    // Facebook event
    // Use metaEventId — the UUID generated client-side at checkout start
    // (also persisted on Stripe subscription metadata) — as the dedup id
    // shared with the server-side Meta CAPI Purchase from the Stripe
    // webhook. We deliberately do NOT use params.purchaseId here: that
    // field is the Stripe subscription id and is also sent to GA, Twitter,
    // Taboola, Snap, … as transaction_id / conversion_id / order_id; those
    // platforms need it to match Stripe exports.
    trackFacebookEvent('Purchase',
      {
        currency: params.currency,
        value: params.value
      },
      params.metaEventId)

    // Appsflyer event
    callAppsFlyer('pba', 'event', {
      eventType: 'EVENT',
      eventName: 'purchase',
      eventRevenue: params.value,
      eventRevenueCurrency: params.currency,
      eventValue: {
        sku: params.pricingId,
        unit_price: params.value,
        currency: params.currency
      }
    })

    gTagService.logPurchase({
      affiliation: params.affiliation,
      currency: params.currency,
      value: params.value,
      items: [params.pricingId],
      transaction_id: params.purchaseId
    })

    trackTwitterEvent(TWITTER_EVENTS.PURCHASE, {
      conversion_id: params.purchaseId,
      currency: params.currency,
      value: params.value,
      description: params.affiliation,
      contents: [{
        content_id: params.pricingId,
        content_name: params.pricingId,
        content_price: params.value.toFixed(2),
        num_items: 1
      }],
      status: 'completed',
      twclid: params.twclid
    })

    taboolaService?.logPurchase({
      orderid: params.purchaseId,
      currency: params.currency,
      revenue: params.value
    })

    snapchatService?.logEvent('PURCHASE', {
      currency: params.currency,
      value: params.value,
      item_ids: [params.pricingId],
      transaction_id: params.purchaseId,
      order_id: params.purchaseId
    })

    // Newsbreak is a service that's only available for US customers, hence they have no currency parameter to denote which currency the value is in
    // So we're now only sending the event if the currency is USD, which is the most likely currency to be used by US customers.
    if (params.currency === 'USD') {
      trackNewsBreakEvent('complete_payment', {
        nb_value: params.value
      })
    }

    /*
    pinterestService?.logPurchase({
      event_id: params.purchaseId,
      order_id: params.purchaseId,
      currency: params.currency,
      value: params.value,
      product_id: params.pricingId
    })
    */
  }

  if (eventName === 'web_startCheckout') {
    const checkoutParams: { channel: string } & CustomPackage = { ...params }

    trackFacebookEvent('InitiateCheckout', {
      currency: checkoutParams.currency,
      value: checkoutParams.price
    })

    taboolaService?.logEvent('startCheckout', {
      orderid: params.pricingId,
      currency: checkoutParams.currency,
      revenue: checkoutParams.price
    })

    snapchatService?.logEvent('START_CHECKOUT', {
      currency: checkoutParams.currency,
      value: checkoutParams.price,
      payment_info_available: 0
    })

    if (checkoutParams.currency === 'USD') {
      trackNewsBreakEvent('initiate_checkout', {
        nb_value: checkoutParams.price.toString()
      })
    }
  }

  if (eventName === 'web_signUpSuccess') {
    trackFacebookEvent('CompleteRegistration')

    callAppsFlyer('pba', 'event', {
      eventType: 'EVENT',
      eventName: 'signup'
    })

    taboolaService?.logEvent('signUpSuccess', {
      orderid: params.pricingId,
      currency: params.currency,
      revenue: params.value
    })

    snapchatService?.logEvent('SIGN_UP', {
      success: 1
    })

    if (params.currency === 'USD') {
      trackNewsBreakEvent('complete_registration', {
        nb_value: params.value
      })
    }
  }

  if (eventName === 'web_commit_1_gender') {
    taboolaService?.logEvent('commitGender')
  }

  if (eventName === 'web_commit_33_email') {
    taboolaService?.logEvent('commitNewsletter')
  }
}

/**
 * Global logEvent function
 */
export function createAnalytics(gTagService: IGTagService, taboolaService: ITaboolaService | null, snapchatService: ISnapchatService | null) { // Pinterest service is removed temporarily.
  const logError = (eventName: AnalyticEvents | string, error: Error | string | null = null, params?: Record<string, any>, logToSentry: boolean = false) => {
    // Create error object if error is null or a string
    if (error == null) {
      error = new Error(eventName)
    } else if (typeof error === 'string') {
      error = new Error(error)
    }

    // While on DEV crash to clearly point you are sending an event that will be truncated
    if (__DEV__) {
      invariant(eventName.length <= 40, `Firebase events must be shorter than 40 characters, event: ${eventName}, length: ${eventName.length}`)
    } else {
      eventName = (eventName.substring(0, 40) as AnalyticEvents)
    }

    if (__DEV__) {
      console.error('logError: ', eventName, error ?? '', params ?? '')
    }

    try {
      gTagService.logEvent(eventName, {
        localId: getLocalId(),
        errorMessage: error.message,
        errorCode: (error as any).code,
        ...params
      })

      if (logToSentry) {
        Sentry.captureException(error, {
          level: 'error',
          extra: {
            eventName,
            ...params
          }
        })
      }
    } catch (error) {
      logEvent('web_logErrorException', { error, eventName, params })
    }
  }

  const logEvent = (eventName: AnalyticEvents | string, params?: Record<string, any>) => {
    // While on DEV crash to clearly point you are sending an event that will be truncated
    if (__DEV__) {
      invariant(eventName.length <= 40, `Firebase events must be shorter than 40 characters, event: ${eventName}, length: ${eventName.length}`)
    } else {
      eventName = (eventName.substring(0, 40) as AnalyticEvents)
    }

    if (__DEV__) {
      console.log('logEvent: ', eventName, params ?? '')
    }

    // firebaseService.logEvent(eventName, params) // gtag is already logging for firebase
    gTagService.logEvent(eventName, {
      localId: getLocalId(),
      ...params
    })

    Sentry.addBreadcrumb({
      category: 'log_event',
      message: eventName,
      level: 'info'
    })

    if (THIRD_PARTY_SUPPORTED_EVENTS.includes((eventName as AnalyticEvents))) { // typescript is complaining that eventName can be also a string. I think, it's not harmful to force it to be AnalyticEvents here.
      try {
        logEventToThirdParty(gTagService, taboolaService, snapchatService, (eventName as AnalyticEvents), params) // Pinterest service is removed temporarily.
      } catch (error) {
        console.error(error)
        // firebaseApp.crashlytics().recordError(error)
      }
    }

    if (eventName === 'web_startCheckout') {
      // This event was previously logged via `logEventToThirdParty`, which required injecting `firebaseServicePromise` just for this log.
      // To simplify and avoid unnecessary async handling, it’s now logged directly here instead.
      // It's unclear if logging this event is still necessary, but preserving it for now.
      logEvent('begin_checkout', params)
    }
  }

  window.logError = logError
  window.logEvent = logEvent

  return {
    logError,
    logEvent
  }
}
